import { type ReactNode } from 'react';
import { isElement, isPortal } from 'react-is';
import { isNullish } from '@/utils/isNullish';

export function createEnumTypeGuard<T>(enumMap: T) {
  return (x: unknown): x is NonNullable<unknown> extends T ? never : T[keyof T] => {
    for (const key in enumMap) {
      if (enumMap[key] === x) {
        return true;
      }
    }
    return false;
  };
}

export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

export function isNumber(value: unknown): value is number {
  return typeof value === 'number';
}

export function isInteger(value: unknown): value is number {
  return isNumber(value) && Number.isInteger(value);
}

export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

export function isObject(value: unknown): value is Record<string, unknown> {
  return !isNullish(value) && !Array.isArray(value) && typeof value === 'object';
}

export function isNumberArray(value: unknown[]): value is number[] {
  return value.every((v) => isNumber(v));
}

export function isReactNode(node: unknown): node is ReactNode {
  if (Array.isArray(node)) {
    return node.every(isReactNode);
  }
  return isElement(node) || isPortal(node) || isString(node) || isNumber(node) || isBoolean(node) || isNullish(node);
}

export function isBlob(value: unknown): value is Blob {
  return value instanceof Blob;
}
